import CustomButton from '../reusable/CustomButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionActions from '@mui/material/AccordionActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useFetchData from '../hooks/useFetchData';
import { getFinalizedTranscriptsDetails } from '../../services/transcriptsService';
import useSchools from '../hooks/useSchools';
import { TRANSCRIPTS_CATEGORIES, ACADEMIC_PERIODS_NAMES } from '../../constants';
import Loader from '../reusable/Loader';
import { getSubjectAreas } from '../../services/eoaConfigServices';
import type { TranscriptsBySubjectAreaType, AcademicPeriodData, Course, AcademicPeriodNames } from '../../interfaces/TranscriptsInterface';
import SubjectAreaTable from './transcriptsBySubjectArea/SubjectAreaTable';
import { SubjectAreaNames } from '../../interfaces/EOAInterface';
import CustomAlert from '../reusable/CustomAlert';
import PrintIcon from '../assets/print.svg';
import PrintContainer from '../reusable/PrintContainer';

import './TranscriptsBySubjectArea.css';

function TranscriptsBySubjectArea() {
  const { transcriptSelectionSchool } = useSchools();

  const { isPending: isTranscriptsDataPending, error: transcriptsDataError, data: transcriptsData } = useFetchData({
    fetchData: (eoaId, access_token) => getFinalizedTranscriptsDetails(eoaId, access_token, transcriptSelectionSchool),
    errorMessage: 'Error occured while fetching transcripts details!',
    initialState: {
      is_finalized: false,
      transcripts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: []
      }
    }
  });

  const { isPending: isSubjectAreasDataPending, error: subjectAreasDataError, data: subjectAreasData } = useFetchData({
    fetchData: (eoaId, access_token) => getSubjectAreas(access_token),
    errorMessage: 'Error occured while fetching transcripts details!',
    initialState: []
  });

  if (isTranscriptsDataPending || isSubjectAreasDataPending) return <Loader />;
  if (transcriptsDataError || subjectAreasDataError) return <p className="center-text">Error occured while fetching transcripts details!</p>;

  const parseData = () => {
    let allTranscripts = (transcriptsData.transcripts.RIGOROUS_ACADEMIC || []).concat(transcriptsData.transcripts.ON_THE_CUSP || []).concat(transcriptsData.transcripts.STRUGGLING || []);
    const parsedData = {} as TranscriptsBySubjectAreaType;

    subjectAreasData.forEach(subjectArea => {
      parsedData[subjectArea] = [];

      allTranscripts.forEach(transcript => {
        let rowData = {
          studentNumber: transcript.school_student_number
        } as AcademicPeriodData;

        (Object.keys(ACADEMIC_PERIODS_NAMES) as AcademicPeriodNames[]).forEach(academicPeriodName => {
          let academicPeriod = transcript.academic_periods.find(academicPeriod => academicPeriod.period === academicPeriodName);
          let courses = [] as Course[];

          if (academicPeriod) {
            academicPeriod.terms.forEach(term => {
              let subjectAreaData = term.subject_areas.find(area => area.name === subjectArea);
              if (subjectAreaData) courses = courses.concat(subjectAreaData.courses);
            });
          }

          rowData[academicPeriodName] = courses;
        });

        parsedData[subjectArea].push(rowData);
      });
    });

    return parsedData;
  };

  const parsedData = parseData();

  const getCoursesCount = (studentData: AcademicPeriodData) => {
    return studentData.NINTH_GRADE.length + studentData.TENTH_GRADE.length + studentData.ELEVENTH_GRADE.length + studentData.TWELFTH_GRADE.length;
  };

  const checkForData = (studentsData: AcademicPeriodData[]) => {
    let totalCoursesCount = 0;

    studentsData.forEach(studentData => {
      totalCoursesCount += getCoursesCount(studentData);
    });

    return totalCoursesCount > 0;
  };

  return (
    <>
      <div className="transcripts-view-header">
        <h2>Transcript view by subject area</h2>
        <PrintContainer
          printButton={(handlePrint) => (
            <CustomButton
              variant="text"
              buttonText="Print All"
              icon={<img src={PrintIcon} alt="download"></img>}
              iconPosition="start"
              buttonAction={handlePrint}
            />
          )}
        >
          { (Object.keys(parsedData) as SubjectAreaNames[]).map(subjectAreaName => (
            checkForData(parsedData[subjectAreaName]) ?
              <div key={subjectAreaName} className="subject-area-page">
                <p>{subjectAreaName}</p>
                <SubjectAreaTable data={parsedData[subjectAreaName]} />
              </div>
              :
              null
          ))
          }
        </PrintContainer>
      </div>
      <div className="transcripts-view-body">
        { (Object.keys(parsedData) as SubjectAreaNames[]).map(subjectAreaName => (
          <Accordion key={subjectAreaName} className="subject-area-accordion">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              {subjectAreaName}
            </AccordionSummary>
            <AccordionDetails className="subject-area-details">
              { checkForData(parsedData[subjectAreaName]) ?
                <SubjectAreaTable data={parsedData[subjectAreaName]} />
                :
                <CustomAlert alertType="warning" header="No classes found in this subject area." />
              }
            </AccordionDetails>
            { checkForData(parsedData[subjectAreaName]) ?
              <AccordionActions>
                <PrintContainer
                  printButton={(handlePrint) => (
                    <CustomButton
                      variant="text"
                      buttonText="Print"
                      icon={<img src={PrintIcon} alt="download"></img>}
                      iconPosition="start"
                      buttonAction={handlePrint}
                    />
                  )}
                >
                  <div className="subject-area-page">
                    <p>{subjectAreaName}</p>
                    <SubjectAreaTable data={parsedData[subjectAreaName]} />
                  </div>
                </PrintContainer>
              </AccordionActions>
              :
              null
            }
          </Accordion>
        ))
        }
      </div>
    </>
  );
}

export default TranscriptsBySubjectArea;